import { FunnelIcon } from '@heroicons/react/24/outline';
import { isEqual } from 'lodash';
import { FC, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useNavigate } from 'hotel/navigation';

import {
  RosterRequestsFilterInput,
  RosterRequestStatus,
  RosterRequestGroup,
  User,
  UserRole,
  useUsersWithProfileQuery,
} from '../../generated/graphql';
import { useAppSelector } from '../../store/store';
import buildUserDisplayName from '../../utilities/buildUserDisplayName';
import getEnumLabel from '../../utilities/getEnumLabel';
import {
  getControlFieldLabelForPod,
  getGroupOptionsForPod,
} from '../../utilities/podBasedFiltering';
import { Button, TealButtonSm } from '../Button';
import FilterChip from '../Chips/FilterChip';
import FilterChipStatus from '../Chips/FilterChipStatus';
import { TableFilterSelectField, TableFilterTextField } from '../Inputs';
import {
  TableFilterReactSelectEnum,
  formatEnumsToOptions,
  parseValuesToEnums,
} from '../Inputs/TableFilterInputs/ReactSelect/TableFilterReactSelectEnum';

// Función para formatear valores de grupo de SNAKE_CASE a formato legible
const formatGroupValue = (value: string): string => {
  if (typeof value === 'string') {
    return value
      .split('_')
      .map((word, index) => {
        // Mantener el formato original para códigos como J4
        if (index === 0 && /^[A-Z][0-9]$/.test(word)) {
          return word;
        }
        // Convertir a Title Case para las demás palabras
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }
  return value;
};

interface FiltersRequestChipContainerProps {
  filters: Partial<RosterRequestsFilterInput>;
  onUpdateFilter: (values: Partial<RosterRequestsFilterInput>) => void;
  onSubmit: (values: Partial<RosterRequestsFilterInput>) => void;
  openSidebar: () => void;
}

const defaultStatusValues = [
  RosterRequestStatus.RequestReceived,
  RosterRequestStatus.InProgress,
  RosterRequestStatus.AwaitingConfirmation,
  RosterRequestStatus.MoreInfoRequired,
  RosterRequestStatus.OverPdApprovalNeeded,
];

const FiltersRequestChipContainer: FC<FiltersRequestChipContainerProps> = ({
  filters,
  onSubmit,
  openSidebar,
}) => {
  const navigate = useNavigate();
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [tempFilters, setTempFilters] =
    useState<Partial<RosterRequestsFilterInput>>(filters);

  const { rosterRequestType, rosterRequestGroups } = useAppSelector(
    (state) => state.enums,
  );
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);

  const { data: usersData } = useUsersWithProfileQuery();

  // Update temp filters when parent filters change
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const handleChipUpdate = (fieldName: string, value: unknown) => {
    if (fieldName === 'status' || fieldName === 'requestType') {
      let arrayValue: string[] = [];

      // Handle empty arrays or null/undefined values
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setTempFilters((prev) => ({
          ...prev,
          [fieldName]: undefined, // Set to undefined to remove the filter
        }));
        return;
      }

      // Process array values
      if (Array.isArray(value)) {
        arrayValue = value.map((v) =>
          typeof v === 'object' && v !== null ? v.value : v,
        );
      } else if (value !== null && value !== undefined) {
        arrayValue = [String(value)];
      }
      setTempFilters((prev) => ({
        ...prev,
        [fieldName]: arrayValue,
      }));
    } else {
      // Handle empty string values
      if (value === '') {
        setTempFilters((prev) => ({
          ...prev,
          [fieldName]: undefined, // Set to undefined to remove the filter
        }));
        return;
      }

      setTempFilters((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    }
  };

  const ensureArrayValue = (value: unknown) => {
    if (Array.isArray(value)) {
      return value;
    }
    if (value === null || value === undefined) {
      return [];
    }
    return [value];
  };

  // Function to format requestType values for display in the chip
  const formatRequestTypeValues = (values: unknown) => {
    if (!values || (Array.isArray(values) && values.length === 0)) {
      return [];
    }

    const arrayValues = ensureArrayValue(values);
    return arrayValues.map((v) => {
      // If it's already an object with label/value, return it
      if (typeof v === 'object' && v !== null && 'value' in v) {
        return v;
      }

      // Otherwise, find the corresponding option
      const option = rosterRequestType.find((opt) => opt.value === v);
      return option || { value: v, label: String(v) };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(tempFilters);
  };

  const handleClearFilters = () => {
    navigate(location.pathname, { replace: true });
    const defaultFilters = {
      status: defaultStatusValues,
      id: undefined,
      controlNumber: undefined,
      requestType: undefined,
      requestedBy: undefined,
      assignedGroup: undefined,
    };
    setTempFilters(defaultFilters);
    onSubmit(defaultFilters);
  };

  return (
    <div className="border border-gray-200 bg-gray-100 p-5 sm:px-5">
      <Form
        onSubmit={onSubmit}
        initialValues={filters}
        initialValuesEqual={(obj1, obj2) => isEqual(obj1, obj2)}
        render={() => (
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* Mobile View */}
            <div className="flex items-center justify-between sm:hidden">
              <h1 className="text-xl font-bold text-primaryBlue">
                Requests View
              </h1>
              <Button
                type="button"
                onClick={openSidebar}
                className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                Filters
                <FunnelIcon className="size-4" />
              </Button>
            </div>

            {/* Desktop View */}
            <div className="hidden flex-wrap items-center gap-2 p-4 sm:flex">
              <div className="flex items-center">
                <FunnelIcon className="size-4" />
              </div>
              <div className="max-w-60 text-ellipsis">
                <FilterChipStatus
                  label="Status"
                  value={
                    Array.isArray(tempFilters.status)
                      ? (tempFilters.status as RosterRequestStatus[])
                      : []
                  }
                  fieldName="status"
                  onUpdate={handleChipUpdate}
                  isEditing={activeChip === 'status'}
                  onEditingChange={(editing) =>
                    setActiveChip(editing ? 'status' : null)
                  }
                />
              </div>
              <FilterChip
                label="Request Type"
                value={formatRequestTypeValues(tempFilters.requestType)}
                fieldName="requestType"
                inputComponent={TableFilterReactSelectEnum}
                inputProps={{
                  labelText: 'Request Type',
                  options: rosterRequestType,
                  isMulti: true,
                  isSearchable: false,
                  hideSelectedOptions: false,
                  parse: parseValuesToEnums,
                  format: formatEnumsToOptions(rosterRequestType),
                  value: formatRequestTypeValues(tempFilters.requestType),
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'requestType'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'requestType' : null)
                }
              />
              <FilterChip
                label="Request ID"
                value={tempFilters.id || ''}
                fieldName="id"
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: 'Request ID',
                  placeholder: 'Enter request ID',
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'id'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'id' : null)
                }
              />
              <FilterChip
                label={getControlFieldLabelForPod(clientPod)}
                value={tempFilters.controlNumber || ''}
                fieldName="controlNumber"
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: getControlFieldLabelForPod(clientPod),
                  placeholder: `Enter ${getControlFieldLabelForPod(clientPod).toLowerCase()}`,
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'controlNumber'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'controlNumber' : null)
                }
              />
              <FilterChip
                label="Requested By"
                value={tempFilters.requestedBy || ''}
                fieldName="requestedBy"
                inputComponent={TableFilterSelectField}
                inputProps={{
                  labelText: 'Requested By',
                  options:
                    usersData?.users.filter(
                      (user) =>
                        user?.roles?.includes(UserRole.ClientUser) ||
                        user?.roles?.includes(UserRole.ClientAdmin),
                    ) ?? [],
                  renderOption: (user: User) => (
                    <option value={user.id}>
                      {buildUserDisplayName(user)}
                    </option>
                  ),
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'requestedBy'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'requestedBy' : null)
                }
              />
              <FilterChip
                label="Group"
                value={tempFilters.assignedGroup || ''}
                fieldName="assignedGroup"
                inputComponent={TableFilterSelectField}
                inputProps={{
                  labelText: 'Group',
                  placeholder: 'Select group',
                  options: getGroupOptionsForPod(clientPod),
                  renderOption: (group: RosterRequestGroup) => (
                    <option value={group}>
                      {getEnumLabel(rosterRequestGroups, group) ||
                        formatGroupValue(group)}
                    </option>
                  ),
                }}
                onUpdate={handleChipUpdate}
                isEditing={activeChip === 'assignedGroup'}
                onEditingChange={(editing) =>
                  setActiveChip(editing ? 'assignedGroup' : null)
                }
              />
              <div className="flex items-center gap-2">
                <Button size="sm" type="button" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
                <TealButtonSm type="submit">Apply Filters</TealButtonSm>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default FiltersRequestChipContainer;
